import pluralize from "pluralize"
import { Optional } from "utility-types"
import moment from "moment"
import type { unitOfTime, Duration } from "moment"

export type DurationConstructor = unitOfTime.DurationConstructor

export type BaseUnitOfTime = unitOfTime.Base

export type { Duration }

export const duration = moment.duration

export { duration as createDuration }

export function fromNow(date: Date | string, withoutSuffix = false) {
	return moment(date).fromNow(withoutSuffix)
}

/**
 * Parses the date, keeping the timezone information
 */
export function parseDate(date: string | Date, format?: string) {
	return moment(date, format).toDate()
}

export function localOrUtcTimestampToLocalDate(
	localDateString: string | undefined,
	utcDateString: string
) {
	if (localDateString) {
		return parseDate(localDateString.slice(0, -1))
	}
	return utcTimestampToLocalDate(utcDateString)
}

export function localOrUtcTimeToLocalDate(
	localTime: string | undefined,
	utcTime: string
) {
	if (localTime) {
		return parseDate(localTime, datetimeConfig.timeFormat)
	}
	return utcTimeToLocalDate(utcTime)
}

export function getDiffBetweenLocalAndUtcTimestamp(
	localDateString: string | undefined,
	utcDateString: string,
	unit: unitOfTime.Diff = "minutes"
) {
	if (!localDateString) {
		return 0
	}
	const localDate = parseDate(localDateString.slice(0, -1))
	const localDateFromUtc = utcTimestampToLocalDate(utcDateString)
	return getDiff(localDate, localDateFromUtc, unit)
}

export function clone(date: Date | string) {
	return moment(date).clone().toDate()
}

export function getDaysInMonth(date: Date | string) {
	return moment(date).daysInMonth()
}

export function getDaysInYear(date: Date) {
	return (
		moment(date)
			.clone()
			.endOf("year")
			.diff(moment(date).clone().startOf("year"), "days") + 1
	)
}

export function formatDate(date: Date | string, template: string) {
	return moment(date).format(template)
}

/** @deprecated Please use formatDate */
export function format(date: Date | string, template: string) {
	return moment(date).format(template)
}

export function toISOString(date: Date | string) {
	return moment(date).clone().toISOString()
}

export function isToday(date: Date | string) {
	return isSame(parseDate(date), new Date(), "day")
}

export function isYesterday(date: Date | string) {
	return isSame(parseDate(date), subtractUnit(new Date(), 1, "day"), "day")
}

export function isTomorrow(date: Date | string) {
	return isSame(parseDate(date), addUnit(new Date(), 1, "day"), "day")
}

export function isSame(
	date1: Date | string,
	date2: Date | string,
	unit?: unitOfTime.StartOf
) {
	return moment(date1).isSame(date2, unit)
}

export function isAfter(
	date1: Date | string,
	date2: Date | string,
	unit?: unitOfTime.StartOf
) {
	return moment(date1).isAfter(date2, unit)
}

export function getUnit(date: Date | string, unit: unitOfTime.All) {
	return moment(date).get(unit)
}

export function setUnit(
	date: Date | string,
	unit: unitOfTime.All,
	value: number
) {
	return moment(date).clone().set(unit, value).toDate()
}

export function addDuration(
	date: Date | string,
	d: ReturnType<typeof duration>
) {
	return moment(date).add(d).toDate()
}

export function setTimeFrom(date: Date | string, from: Date | string) {
	from = parseDate(from)
	return setUnit(
		setUnit(
			setUnit(parseDate(date), "hours", getUnit(from, "hours")),
			"minutes",
			getUnit(from, "minutes")
		),
		"seconds",
		getUnit(from, "seconds")
	)
}

export function addUnit(
	date: Date | string,
	value: number,
	unit: unitOfTime.DurationConstructor
) {
	return moment(date).clone().add(value, unit).toDate()
}

export function subtractUnit(
	date: Date | string,
	value: number,
	unit: unitOfTime.DurationConstructor
) {
	return moment(date).clone().subtract(value, unit).toDate()
}

export function isBetween(
	date: Date | string,
	min: Date | string,
	max: Date | string,
	unit?: unitOfTime.StartOf,
	d?: "[]" | "()" | "(]" | "[)"
) {
	return moment(date).isBetween(min, max, unit, d)
}

export function isSameOrAfter(
	date: Date | string,
	comparedTo: Date | string,
	unit?: unitOfTime.StartOf
) {
	return moment(date).isSameOrAfter(comparedTo, unit)
}

export function isSameOrBefore(
	date: Date | string,
	comparedTo: Date | string,
	unit?: unitOfTime.StartOf
) {
	return moment(date).isSameOrBefore(comparedTo, unit)
}

export function isBefore(
	date: Date | string,
	comparedTo: Date | string = new Date(),
	unit?: unitOfTime.StartOf
) {
	return moment(date).isBefore(comparedTo, unit)
}

export function startOf(date: Date | string, unit: unitOfTime.StartOf) {
	return moment(date).clone().startOf(unit).toDate()
}

export function endOf(date: Date | string, unit: unitOfTime.StartOf) {
	return moment(date).clone().endOf(unit).toDate()
}

export const datetimeConfig = {
	dateDisplayFormat: "D MMM, YYYY",
	dateTimeDayDisplayFormat: "dddd D MMM, YYYY [at] hh:mm A",
	dateTimeDisplayFormat: "D MMM, YYYY [at] hh:mm A",
	timeDisplayFormat: "hh:mm A",
	timeFormat: "HH:mm:ss",
	timestampDateFormat: "YYYY-MM-DD HH:mm:ss",
}

export function configureDatetime(config: typeof datetimeConfig) {
	datetimeConfig.dateDisplayFormat = config.dateDisplayFormat
	datetimeConfig.timeDisplayFormat = config.timeDisplayFormat
	datetimeConfig.timestampDateFormat = config.timestampDateFormat
	datetimeConfig.timeFormat = config.timeFormat
}

/**
 * Converts a given utc timestamp to local datetime
 */
export function utcTimestampToLocalDate(utcDate: string) {
	return moment.utc(utcDate).clone().local().toDate()
}

/**
 * Given a UTC date, convert it to local string
 */
export function utcTimestampToLocalDateString(
	utcDate: string,
	template: string = datetimeConfig.dateDisplayFormat
) {
	return moment(utcTimestampToLocalDate(utcDate)).format(template)
}

/**
 * Given a UTC date, convert it to local date/time string
 */
export function utcTimestampToLocalDateTimeString(utcDate: string) {
	return moment(utcTimestampToLocalDate(utcDate)).format(
		datetimeConfig.dateDisplayFormat +
			" [at] " +
			datetimeConfig.timeDisplayFormat
	)
}

/**
 * Given a UTC date, convert it to local time string
 */
export function utcTimestampToLocalTimeString(utcDate: string) {
	return moment(utcTimestampToLocalDate(utcDate)).format(
		datetimeConfig.timeDisplayFormat
	)
}

/**
 * Formats a given date to server's timestamp Y-m-d H:i:s
 */
export function dateToTimestampFormat(date: string | Date): string {
	return moment(date).format(datetimeConfig.timestampDateFormat)
}

/**
 * Converts a given local date to UTC timestamp string
 */
export function dateToUTCString(
	localDate: string | Date,
	format = datetimeConfig.timestampDateFormat
): string {
	return moment(localDate).clone().utc().format(format)
}

export function utcTimeToLocalDate(utcTime: string) {
	return moment.utc(utcTime, datetimeConfig.timeFormat).clone().local().toDate()
}

export function utcTimeToLocalTimeString(utcTime: string) {
	return moment(utcTimeToLocalDate(utcTime)).format(
		datetimeConfig.timeDisplayFormat
	)
}

export function timeToUTCString(localTime: string | Date) {
	return moment(localTime, datetimeConfig.timeFormat)
		.clone()
		.utc()
		.format(datetimeConfig.timeFormat)
}

export function parseDateFromQuery(
	date: string,
	format = "YYYY-MM-DD",
	fallback?: Date
): Date {
	const parsedDate = moment(date, format)
	if (parsedDate.isValid()) return parsedDate.toDate()
	return fallback || new Date()
}

/**
 * Converts a given date to query (location) params
 */
export function dateToQuery(
	date: Date | string,
	format = "YYYY-MM-DD"
): string {
	return moment(date).format(format)
}

export function getDiff(
	date1: Date | string,
	date2: Date | string,
	unit?: unitOfTime.Diff,
	precise?: boolean
) {
	return moment(date1).diff(date2, unit, precise)
}

type TOptions = {
	/**
	 * Use short names
	 */
	short: boolean
}
const unitMeasures = {
	y: 31557600000,
	mo: 2629800000,
	w: 604800000,
	d: 86400000,
	h: 3600000,
	m: 60000,
	s: 1000,
	ms: 1,
}
const units: Array<keyof typeof unitMeasures> = [
	"y",
	"mo",
	"w",
	"d",
	"h",
	"m",
	"s",
]

function getNameForUnit(
	unit: (typeof units)[0],
	value: number,
	short = false
): string {
	if (short) return unit
	let fullName: string
	switch (unit) {
		case "m":
			fullName = "minute"
			break
		case "s":
			fullName = "second"
			break
		case "h":
			fullName = "hour"
			break
		case "d":
			fullName = "day"
			break
		case "w":
			fullName = "week"
			break
		case "ms":
			fullName = "month"
			break
		case "y":
			fullName = "year"
			break
		default:
			fullName = ""
	}
	return pluralize(fullName, value)
}

export function humanizeDuration(
	ms: number,
	options?: Optional<TOptions>
): string {
	const mergedOptions: TOptions = { short: true, ...options }
	ms = Math.abs(ms)
	const pieces: Array<{ unitCount: number; unitName: (typeof units)[0] }> = []
	units.forEach((unitName) => {
		const unitMS: number = unitMeasures[unitName]
		const unitCount = Math.floor(ms / unitMS)
		if (unitCount > 0) {
			pieces.push({
				unitCount: unitCount,
				unitName: unitName,
			})
		}
		ms -= unitCount * unitMS
	})
	return pieces
		.map(
			({ unitCount, unitName }) =>
				`${unitCount}${getNameForUnit(
					unitName,
					unitCount,
					mergedOptions.short
				)}`
		)
		.join(" ")
}

/**
 * formate not supported
 * D MMM, YYYY [at] hh:mm A
 * dddd D MMM, YYYY [at] hh:mm A
 */

/**
 * Converts a UTC time string to a local time string based on a specific timezone.
 *
 * Logic:
 * 1. Parse the UTC string into a Moment object.
 * 2. Convert to a native Date object.
 * 3. Use `toLocaleString` with the target timezone to get a string representation of the time in that zone.
 * 4. Parse that string back into a Date object (effectively "shifting" the time to wall-clock time).
 * 5. Format using Moment.
 * return format: datetimeConfig.timestampDateFormat // "YYYY-MM-DD HH:mm:ss"
 */
export function convertUtcToLocalAccordingToTimezone(
	utcTime: string | Date,
	timeZone?: string
): string {
	const format = datetimeConfig.timestampDateFormat // "YYYY-MM-DD HH:mm:ss"

	// Strip timezone information to treat input as wall-clock time
	// This regex removes timezone info like "GMT+0530", "Z", timezone names, etc.

	// Convert Date objects to ISO string first for proper parsing
	let utcTimeStr: string
	if (typeof utcTime === "string") {
		utcTimeStr = utcTime
	} else {
		// Convert Date to ISO string, which is a standard format
		utcTimeStr = utcTime.toISOString()
	}

	// Remove timezone info from the string
	const cleanedTime = utcTimeStr
		.replace(/GMT[+-]\d{4}/gi, "") // Remove GMT+0530, GMT-0800, etc.
		.replace(/\([^)]*\)/g, "") // Remove timezone names in parentheses like (India Standard Time)
		.replace(/Z$/i, "") // Remove trailing Z
		.replace(/T/, " ") // Replace T with space for consistency
		.replace(/\.\d{3,6}/, "") // Remove milliseconds/microseconds
		.trim()

	// Parse as UTC with the standard format to avoid deprecation warning
	const utcMoment = moment.utc(
		cleanedTime,
		datetimeConfig.timestampDateFormat,
		true
	)
	if (!utcMoment.isValid()) {
		// Fallback: try parsing without strict mode
		const fallbackMoment = moment.utc(cleanedTime)
		if (!fallbackMoment.isValid()) {
			return "Invalid Date"
		}
		// Use fallback moment for the rest of the function
		const utcDate = new Date(fallbackMoment.toDate())
		try {
			if (timeZone) {
				const localizedTime = new Date(
					utcDate.toLocaleString("en-US", { timeZone })
				)
				if (isNaN(localizedTime.getTime())) {
					return fallbackMoment.format(format)
				}
				return moment(localizedTime).format(format)
			} else {
				return fallbackMoment.format(format)
			}
		} catch (e) {
			return fallbackMoment.format(format)
		}
	}

	const utcDate = new Date(utcMoment.toDate())

	try {
		// If a timezone is provided, attempt to convert
		if (timeZone) {
			// Convert the UTC date to a string in the target timezone, then parse it back unique to that "wall clock" time.
			// Note: This relies on the environment's locale interpretation, but "en-US" usually works for standard formats.
			const localizedTime = new Date(
				utcDate.toLocaleString("en-US", { timeZone })
			)

			// Check if localizedTime is valid
			if (isNaN(localizedTime.getTime())) {
				// If conversion fails (e.g. invalid result), fallback to original time
				return utcMoment.format(format)
			}

			const formattedLocalTime = moment(localizedTime).format(format)
			return formattedLocalTime
		} else {
			// If no timezone is provided, simply return the input time formatted
			return utcMoment.format(format)
		}
	} catch (e) {
		// Fallback to original time if timezone is invalid or any other error occurs
		return utcMoment.format(format)
	}
}

/**
 * FIXED: convertLocalToUtcAccordingToTimezone
 *
 * Logic:
 * 1. Parse the input "Local" time string as if it were UTC (getting a numeric timestamp).
 * 2. Calculate the timezone offset for that specific time by comparing:
 *    - The time we *want* (Target Local)
 *    - The time that timestamp *actually* represents in the target zone (Actual Local).
 * 3. Adjust the timestamp by the difference.
 * return format: datetimeConfig.timestampDateFormat // "YYYY-MM-DD HH:mm:ss"
 */
export function convertLocalToUtcAccordingToTimezone(
	localTime: string | Date,
	timeZone?: string
): string {
	// Strip timezone information to treat input as wall-clock time
	// Convert Date objects to ISO string first for proper parsing
	let localTimeStr: string
	if (typeof localTime === "string") {
		localTimeStr = localTime
	} else {
		// Convert Date to ISO string, which is a standard format
		localTimeStr = localTime.toISOString()
	}

	// Remove timezone info from the string
	const cleanedTime = localTimeStr
		.replace(/GMT[+-]\d{4}/gi, "") // Remove GMT+0530, GMT-0800, etc.
		.replace(/\([^)]*\)/g, "") // Remove timezone names in parentheses like (India Standard Time)
		.replace(/Z$/i, "") // Remove trailing Z
		.replace(/T/, " ") // Replace T with space for consistency
		.replace(/\.\d{3,6}/, "") // Remove milliseconds/microseconds
		.trim()

	// Parse as UTC with the standard format to avoid deprecation warning
	const parsedMoment = moment.utc(
		cleanedTime,
		datetimeConfig.timestampDateFormat,
		true
	)
	if (!parsedMoment.isValid()) {
		// Fallback: try parsing without strict mode
		const fallbackMoment = moment.utc(cleanedTime)
		if (!fallbackMoment.isValid()) {
			return "Invalid Date"
		}
		localTime = fallbackMoment.format(datetimeConfig.timestampDateFormat)
	} else {
		localTime = parsedMoment.format(datetimeConfig.timestampDateFormat)
	}
	try {
		if (!timeZone) return localTime

		// 1. Parse input as if it were UTC to get a baseline timestamp
		// Remove 'Z' to treat it as wall-clock time
		const cleanIso = localTime.replace("Z", "")
		const targetLocalAsUtc = new Date(cleanIso + "Z")

		if (isNaN(targetLocalAsUtc.getTime())) return "Invalid Date"

		// 2. We need to find the offset.
		// Let's verify what time 'targetLocalAsUtc' *actually* is in the target timezone.
		// If we want 09:00 NY, and we pass 09:00 UTC, the system might say it's 04:00 NY.

		// Helper to get the "Wall Clock" time of a timestamp in a specific zone, returned as a UTC timestamp
		const getWallClockAsUtc = (ts: number, tz: string) => {
			const formatter = new Intl.DateTimeFormat("en-US", {
				timeZone: tz,
				year: "numeric",
				month: "numeric",
				day: "numeric",
				hour: "numeric",
				minute: "numeric",
				second: "numeric",
				hour12: false,
			})
			// Create a string that Date.parse() can read as UTC: "MM/DD/YYYY, HH:mm:ss" -> Parse as UTC?
			// Actually, simpler to map parts.
			const parts = formatter.formatToParts(new Date(ts))
			const p = (type: string) =>
				parts.find((v) => v.type === type)?.value || "0"

			// Construct ISO string for the Wall Clock time: YYYY-MM-DDTHH:mm:ssZ
			// We use 'Z' here to say "Treat these wall clock numbers as a UTC timestamp" for easy math
			const iso = `${p("year")}-${p("month").padStart(2, "0")}-${p("day").padStart(2, "0")}T${p("hour").padStart(2, "0")}:${p("minute").padStart(2, "0")}:${p("second").padStart(2, "0")}Z`
			return new Date(iso).getTime()
		}

		let currentGuess = targetLocalAsUtc.getTime()

		// Iterative refinement (usually converges in 1-2 steps) helps handle DST transitions robustly
		for (let i = 0; i < 3; i++) {
			const wallClockInZone = getWallClockAsUtc(currentGuess, timeZone)
			const diff = targetLocalAsUtc.getTime() - wallClockInZone

			// If the difference is 0, our guess maps perfectly to the target wall clock time
			if (diff === 0) break

			// Adjust the guess
			currentGuess += diff
		}
		return moment
			.utc(new Date(currentGuess))
			.format(datetimeConfig.timestampDateFormat)
	} catch (error) {
		console.error("Conversion Error:", error)
		return localTime
	}
}

export function getTimezoneOffset(date: string | Date, timezone: string) {
	const dateOject = typeof date === "string" ? new Date(date) : date

	try {
		const datetimezone = new Intl.DateTimeFormat(undefined, {
			timeZoneName: "longOffset",
			timeZone: timezone,
		}).format(dateOject)
		const dateWithTimezone = datetimezone.split(", GMT")
		const timezoneOffset = dateWithTimezone[1]
		return timezoneOffset ? timezoneOffset : "+00:00"
	} catch (e) {
		return undefined
	}
}
